// ---------------------------------------------------------------------------
// The JAMA page offers the same languages as judojama.com (23): the 9 site
// languages use landing[lang].jama, the 14 extra ones have only the JAMA
// page copy (locales/<code>/jama.json, translations of the English block).
// Navigation and footer stay in the site language for the extra ones.
// ---------------------------------------------------------------------------
import landing, { LandingCopy } from "config/i18n/landing";
import { isLang } from "config/i18n/languages";
import cs from "./locales/cs/jama.json";
import da from "./locales/da/jama.json";
import hr from "./locales/hr/jama.json";
import hu from "./locales/hu/jama.json";
import nl from "./locales/nl/jama.json";
import no from "./locales/no/jama.json";
import pt from "./locales/pt/jama.json";
import ro from "./locales/ro/jama.json";
import fi from "./locales/fi/jama.json";
import sv from "./locales/sv/jama.json";
import tr from "./locales/tr/jama.json";
import bg from "./locales/bg/jama.json";
import uk from "./locales/uk/jama.json";
import ar from "./locales/ar/jama.json";

export type JamaCopy = LandingCopy["jama"];

/** Same order as the language menu of judojama.com: alphabetical by native
 *  name (Latin, then Cyrillic, Arabic, CJK). */
export const JAMA_LANGS = [
  { code: "cs", label: "Čeština" },
  { code: "da", label: "Dansk" },
  { code: "de", label: "Deutsch" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "hr", label: "Hrvatski" },
  { code: "it", label: "Italiano" },
  { code: "hu", label: "Magyar" },
  { code: "nl", label: "Nederlands" },
  { code: "no", label: "Norsk" },
  { code: "pl", label: "Polski" },
  { code: "pt", label: "Português" },
  { code: "ro", label: "Română" },
  { code: "fi", label: "Suomi" },
  { code: "sv", label: "Svenska" },
  { code: "tr", label: "Türkçe" },
  { code: "bg", label: "Български" },
  { code: "uk", label: "Українська" },
  { code: "ar", label: "العربية" },
  { code: "zh", label: "中文" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
] as const;

export type JamaLang = (typeof JAMA_LANGS)[number]["code"];

export const isJamaLang = (value: string | null): value is JamaLang =>
  JAMA_LANGS.some((item) => item.code === value);

const EXTRA = { cs, da, hr, hu, nl, no, pt, ro, fi, sv, tr, bg, uk, ar };
const extra: Record<keyof typeof EXTRA, JamaCopy> = EXTRA;

export const jamaCopy = (lang: JamaLang): JamaCopy =>
  isLang(lang) ? landing[lang].jama : extra[lang as keyof typeof EXTRA];

/** lang attribute value: written variants where the code alone is vague. */
export const jamaHtmlLang = (lang: JamaLang): string =>
  (({ zh: "zh-Hans", no: "nb", pt: "pt-PT" }) as Record<string, string>)[
    lang
  ] || lang;

export const isRtl = (lang: string): boolean => lang === "ar";
